
import BasePageTemplate from "@/components/templates/BasePageTemplate.vue";
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import ContractProfileTemplatePanel from "@/components/organisms/ContractProfileTemplatePanel.vue";
import TableInline from "@/components/atoms/TableInline.vue";
import ReadonlyCheckbox from "@/components/atoms/ReadonlyCheckbox.vue";
import Swal from "sweetalert2";
import IconBtns from "@/components/molecules/IconBtns";
import IconBtn from "@/components/molecules/IconBtn.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import ErrorPanel from "@/components/molecules/ErrorPanel.vue";
import AppHeader from "@/components/atoms/AppHeader.vue";
import TradeContractView, {emptyTradeContractView} from "@/components/pages/contract/TradeContract/TradeContractView";
import TradeContractFormatView from "@/components/pages/contract/TradeContractFormat/TradeContractFormatView";
import ContractProfileTemplateForm from "@/components/organisms/ContractProfileTemplateForm.vue";
import {defineComponent, PropType} from "vue";
import TradeContractBaseView from "@/components/pages/contract/TradeContract/TradeContractBaseView";
import {ContractProfile} from "@/store/modules/hcs/contractProfile/model";
import {ContractProfileTemplate} from "@/store/modules/hcs/contractProfileTemplate/model";
import {Step} from "@/components/templates/StepperPageTemplate.vue";

interface ContractProfileTemplateView extends Omit<ContractProfileTemplate, 'id' | 'terminalId' | 'createdAt' | 'updatedAt'> {
  id: null | string;
  selected: boolean;
  terminalId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export default defineComponent({
  name: "ContractProfileTemplate",
  components: {
    ContractProfileTemplateForm,
    AppHeader,
    ErrorPanel,
    TextFieldLike,
    IconBtn,
    IconBtns,
    ReadonlyCheckbox,
    TableInline,
    ContractProfileTemplatePanel,
    SimpleDialog,
    BasePageTemplate,
  },
  props: {
    tradeContracts: {
      type: Array as PropType<TradeContractBaseView[]>,
      required: true
    },
    tradeContractIndex: {
      type: Number,
      default: 1
    },
    editable: {
      type: Boolean
    },
    steps: {
      type: Array as PropType<Step[]>,
      required: true,
    },
    step: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      editDialog: false as false | ContractProfileTemplateView,
    };
  },
  created() {
    this.loadContractProfileTemplates({});
  },
  computed: {
    ...mapState('hcs/contractProfileTemplate', ['contractProfileTemplates']),
    ...mapGetters({
      hcsClient: 'hcs/client',
    }),
    tradeContract(): TradeContractBaseView {
      return this.tradeContracts[this.tradeContractIndex - 1] ?? emptyTradeContractView();
    },
    isTradeContractFormatView() {
      return this.tradeContract instanceof TradeContractFormatView;
    },
    isTradeContractView() {
      return this.tradeContract instanceof TradeContractView;
    },
    contractProfiles(): ContractProfile[] {
      return this.tradeContract.contractProfiles;
    },
    contractProfileTemplateView(): ContractProfileTemplateView[] {
      const contractProfileTemplates = this.contractProfileTemplates as ContractProfileTemplate[];
      const view: ContractProfileTemplateView[] = contractProfileTemplates.map(cpt => ({
        ...cpt,
        selected: cpt.id === this.tradeContract.contractProfileTemplateId
      }));
      view.unshift({
        id: null,
        name: 'なし',
        description: '契約テンプレートを使用しません',
        selected: this.tradeContract.contractProfileTemplateId === null
      });
      return view;
    },
    errors() {
      const errors = [];
      if(this.contractProfiles.length === 0) {
        errors.push({
          level: 'warn',
          message: '契約文書・契約条項・契約事項のいずれか1つ以上が必要です。契約テンプレートの選択などを行って下さい。'
        });
      } else {
        errors.push({
          level: 'good',
          message: `${this.nextPageName}に進むことができます。`
        });
      }
      return errors;
    },
    contractProfileTemplatePanel(): null | InstanceType<typeof ContractProfileTemplatePanel> {
      return this.$refs.contractProfileTemplatePanel as (null | InstanceType<typeof ContractProfileTemplatePanel>);
    },
    nextPageName(): string {
      return this.steps[this.step + 1]?.label ?? '送信設定';
    },
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar',
      setContractProfiles: 'hcs/contractProfile/setContractProfiles',
    }),
    ...mapActions({
      loadContractProfiles: 'hcs/contractProfile/findList',
      loadContractProfileTemplates: 'hcs/contractProfileTemplate/findList',
    }),
    openEditDialog(row: ContractProfileTemplateView) {
      this.editDialog = row;
      this.$nextTick(() => {
        this.contractProfileTemplatePanel?.load();
      });
    },
    closeEditDialog(apply: boolean) {
      if(apply) {
        this.contractProfileTemplatePanel?.save()
            .then(() => {
              this.editDialog = false;
            });
      } else {
        this.editDialog = false;
      }
    },
    async setSelected(row: ContractProfileTemplateView) {
      const contractProfileTemplateId = row.id;
      const tradeContractId = this.tradeContract.id;

      if(this.contractProfiles.length > 0) {
        const html = [];
        if(row.id) {
          html.push(`契約テンプレートを <strong>${row.name}</strong> へ変更します。`);
          html.push(`現在登録されている契約文書・契約条項・契約事項がすべて<span style="color:darkred;font-weight:bold;">削除され</span>、`);
          html.push(`新しい契約テンプレートのものに置き換えられます。`);
        } else {
          html.push(`契約テンプレート<strong>なし</strong>が選択されました。`);
          html.push(`現在登録されている契約文書・契約条項・契約事項がすべて<span style="color:darkred;font-weight:bold;">削除されます。</span>`);
        }
        html.push('この変更は即時反映され取り消すことができません。本当に変更しますか？');

        const {value: confirm} = await Swal.fire({
          title: '契約テンプレートの変更',
          html: html.join('<br>'),
          icon: 'warning',
          showCloseButton: true,
          showCancelButton: true,
          focusConfirm: true,
          confirmButtonText: "はい",
          cancelButtonText: "いいえ",
          width: '750px',
        });
        if (!confirm) {
          return;
        }
      }

      const loading = this.$loading({
        lock: true,
        text: '処理中...',
        spinner: 'el-icon-loading',
      });

      try {
        // TradeContract の契約プロファイルテンプレートを即時反映する
        this.tradeContract.contractProfileTemplateId = contractProfileTemplateId;
        if(this.tradeContract instanceof TradeContractView) {
          const {data: dstContractProfiles} = await this.hcsClient.post(`/trade_contracts/${tradeContractId}/apply_contract_profile_template`, {
            templateId: contractProfileTemplateId
          });
          this.contractProfiles.splice(0, this.contractProfiles.length, ...dstContractProfiles);
        } else if(this.tradeContract instanceof TradeContractFormatView) {
          const {data: dstContractProfiles} = await this.hcsClient.post(`/trade_contract_formats/${tradeContractId}/apply_contract_profile_template`, {
            templateId: contractProfileTemplateId
          });
          this.contractProfiles.splice(0, this.contractProfiles.length, ...dstContractProfiles);
        } else {
          throw new Error(`unknown TradeContractView: ${this.tradeContract.constructor.name}`);
        }
      } finally {
        loading.close();
      }
    }
  },
});
